import {
  Identify,
  Types,
  identify,
  init,
  reset,
  setGroup,
  setOptOut,
  setUserId,
  track,
} from "@amplitude/analytics-browser";
import type { ServerZoneType } from "@amplitude/analytics-core";
import Configuration from "config";
import { User } from "services/users/model";

import { useEffect, useRef } from "react";
import { enhanceUser } from "services/users";
import { AnalyticsEvent } from "./schema";

let initialized = false;

// The latest identity passed to identifyUser, kept so it can be replayed once
// Amplitude is (re)initialized. Consent — and therefore init — can arrive after
// the user was already identified (logged in before accepting the banner), and
// a mid-session revoke clears the stored identity via reset(); without a replay
// those post-consent events would be anonymous.
let pendingUser: User | null = null;

const canUseAmplitude = () => initialized && Boolean(Configuration.AMPLITUDE_API_KEY);

type EventProps<TName extends AnalyticsEvent["name"]> = Omit<
  Extract<AnalyticsEvent, { name: TName }>,
  "name"
>;

// Only called with statistics consent (see bindConsent in src/index.tsx), so
// a re-init after a mid-session revoke+regrant just lifts the opt-out.
export function initProductAnalytics() {
  const apiKey = Configuration.AMPLITUDE_API_KEY;
  if (!apiKey) {
    return;
  }

  if (initialized) {
    setOptOut(false);
  } else {
    init(apiKey, undefined, {
      logLevel: import.meta.env.PROD ? Types.LogLevel.Error : Types.LogLevel.Warn,
      serverZone: (Configuration.AMPLITUDE_SERVER_ZONE as ServerZoneType) ?? undefined,
      defaultTracking: {
        sessions: false,
        pageViews: true,
        formInteractions: true,
        fileDownloads: false,
      },
    });

    initialized = true;
  }

  // Re-apply an identity captured before consent (or cleared by a prior revoke)
  // so events after consent are attributed rather than anonymous.
  if (pendingUser) {
    identifyUser(pendingUser);
  }
}

// Stops event collection if statistics consent is withdrawn after init, and
// clears the stored user/device identity so the withdrawal also removes the
// identifier linking past and future activity.
export function disableProductAnalytics() {
  if (initialized) {
    setOptOut(true);
    reset();
  }
}

export function identifyUser(user: User) {
  // Remember the identity even when Amplitude isn't usable yet, so consent
  // arriving later can replay it (see initProductAnalytics).
  pendingUser = user;

  if (!canUseAmplitude()) {
    return;
  }

  setUserId(user.id);

  const identifyObj = new Identify();
  const props = Object.entries(buildUserProperties(user));
  for (const [key, value] of props) {
    identifyObj.set(key, value as any);
  }

  identify(identifyObj);

  if (user.company?.id) {
    setGroup("company", user.company.id);
  }
  if (user.company?.teams[0]) {
    setGroup("team", user.company.teams[0].id);
  }
}

function buildUserProperties(user: User) {
  const { student, ...enhancedUser } = enhanceUser(user);
  const activePlan = student?.subscription_plan;

  return {
    user_type: enhancedUser.company ? "company" : "consumer",
    company_name: enhancedUser.company?.name,
    company_id: enhancedUser.company?.id,

    student_type: student?.type,
    student_status: student?.status,
    has_all_onboarding_data: student?.has_all_onboarding_data,
    active_plan: activePlan?.pricing_model?.code,
    plan_period: activePlan?.pricing_model_item?.name,

    hasAccess: student?.has_pro_access,
    accessFlags: student?.access,
    ai_credits_available: (student?.credits.ai_credits ?? 0) > 0,
    session_credits_available: (student?.credits?.remaining ?? 0) > 0,
  };
}

export function trackEvent<TName extends AnalyticsEvent["name"]>(
  eventName: TName,
  ...[props]: keyof EventProps<TName> extends never ? [] : [props: EventProps<TName>]
) {
  if (canUseAmplitude()) {
    track(eventName, props);
  }
}

type UseTrackingOptions = {
  enabled?: boolean;
};

/**
 * Tracks the latest event whenever one of the provided `deps` changes.
 *
 * `deps` are the reactive trigger for the effect. `event` is intentionally kept
 * non-reactive via a ref so the effect can read the latest payload without
 * re-running for every event object change. This is a React 18 substitute for
 * the `useEffectEvent` pattern until the app upgrades.
 *
 * If a field should cause re-tracking when it changes, include it in `deps`.
 * For one-shot tracking, pass an empty dependency list.
 */
export function useTracking<TEvent extends AnalyticsEvent>(
  event: TEvent,
  deps: React.DependencyList,
  options?: UseTrackingOptions
) {
  const { enabled = true } = options ?? {};

  const eventRef = useRef(event);
  eventRef.current = event;

  useEffect(
    () => {
      if (enabled && canUseAmplitude()) {
        const { name, ...props } = eventRef.current;
        const payload = Object.keys(props).length === 0 ? undefined : props;
        track(name, payload);
      }
    },
    // FIXME switch to EffectEvent once we upgrade React
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, ...deps]
  );
}
